import { useTranslation } from '@supply-mover-2-0/web-ui';

/**
 * Invoice row chips shared by every surface that renders an invoice card —
 * the invoices list, the recent-invoices widget, and statement detail rows.
 * Centralized so "what does Paid / Unpaid / Partial look like" (and, with
 * PD-14697, "how urgent is this invoice") stays identical across surfaces.
 */

/** The subset of the invoice list DTO the chips derive their state from. */
export interface InvoiceChipFieldsModel {
	outstandingBalance: string | null;
	total: string | null;
}

type PaymentStatus = 'paid' | 'unpaid' | 'partial';

/**
 * Derive the payment status from the outstanding balance the same way the
 * API's paymentStatus filter does: no balance → paid, full balance →
 * unpaid, anything between → partial.
 */
export function derivePaymentStatus(
	invoice: InvoiceChipFieldsModel,
): PaymentStatus {
	if (
		invoice.outstandingBalance == null ||
		parseFloat(invoice.outstandingBalance) === 0
	) {
		return 'paid';
	}
	if (invoice.total != null && invoice.outstandingBalance === invoice.total) {
		return 'unpaid';
	}
	return 'partial';
}

const STATUS_LABEL: Record<PaymentStatus, string> = {
	paid: 'Paid',
	unpaid: 'Unpaid',
	partial: 'Partial',
};

const STATUS_CLASSES: Record<PaymentStatus, string> = {
	paid: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
	unpaid: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300',
	partial:
		'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
};

/**
 * Payment-status pill (Paid / Unpaid / Partial). Callers skip it for credit
 * memos — payment status is meaningless there and the "Credit Memo" label
 * already identifies the row.
 */
export function InvoicePaymentStatusPill({
	invoice,
}: {
	invoice: InvoiceChipFieldsModel;
}): JSX.Element {
	const { t } = useTranslation();
	const status = derivePaymentStatus(invoice);
	return (
		<span
			className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_CLASSES[status]}`}
		>
			{t(STATUS_LABEL[status])}
		</span>
	);
}

/** Parse the date part of an ISO date / datetime string as a LOCAL date
 *  (avoids the UTC-midnight shift `new Date('YYYY-MM-DD')` would apply). */
function parseDateOnly(value: string): Date | null {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
	if (m == null) {
		return null;
	}
	return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Whole days from today (local midnight) to the given due date.
 *  0 = due today, negative = overdue. Exported for the chip + any surface
 *  that needs the same urgency math. */
export function daysUntilDue(dueDate: string): number | null {
	const due = parseDateOnly(dueDate);
	if (due == null) {
		return null;
	}
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/** Amber window: due today or within the next 7 days. */
const DUE_SOON_DAYS = 7;

const DUE_CHIP_AMBER =
	'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300';
const DUE_CHIP_RED =
	'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300';
const DUE_CHIP_NEUTRAL = 'bg-brand-input text-gray-700 dark:text-gray-200';

/**
 * Relative due-date chip for unpaid / partially-paid invoices (PD-14697):
 * "Overdue N days" (red), "Due today" / "Due in N days" (amber within a
 * week), or a neutral "Due <date>" for the far future. Renders nothing for
 * paid invoices (the green pill is the whole story) or when the due date
 * is absent/unparseable — callers can render it unconditionally.
 */
export function InvoiceDueChip({
	invoice,
}: {
	invoice: InvoiceChipFieldsModel & { dueDate: string | null };
}): JSX.Element | null {
	const { t } = useTranslation();
	if (derivePaymentStatus(invoice) === 'paid') {
		return null;
	}
	if (invoice.dueDate == null || invoice.dueDate === '') {
		return null;
	}
	const days = daysUntilDue(invoice.dueDate);
	if (days == null) {
		return null;
	}

	let classes = DUE_CHIP_NEUTRAL;
	let label: string;
	if (days < 0) {
		classes = DUE_CHIP_RED;
		label =
			days === -1
				? `${t('Overdue')} 1 ${t('day')}`
				: `${t('Overdue')} ${-days} ${t('days')}`;
	} else if (days === 0) {
		classes = DUE_CHIP_AMBER;
		label = t('Due today');
	} else if (days <= DUE_SOON_DAYS) {
		classes = DUE_CHIP_AMBER;
		label =
			days === 1 ? t('Due tomorrow') : `${t('Due in')} ${days} ${t('days')}`;
	} else {
		// Far-future: a neutral date reads better than "Due in 43 days".
		const due = parseDateOnly(invoice.dueDate);
		label = `${t('Due')} ${
			due != null
				? due.toLocaleDateString(undefined, {
						month: 'short',
						day: 'numeric',
					})
				: invoice.dueDate
		}`;
	}

	return (
		<span
			className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${classes}`}
		>
			{label}
		</span>
	);
}
